/**
 * EnrollmentList Component
 * Lista de enrollments pendientes
 */

import { Box, Card, CardContent, Grid, Typography, CircularProgress } from '@mui/material';
import { InboxOutlined as InboxIcon } from '@mui/icons-material';
import { EnrollmentCard } from './EnrollmentCard';
import type { Enrollment } from '@/types/enrollment';

interface EnrollmentListProps {
  enrollments: Enrollment[];
  loading?: boolean;
  onApprove?: (enrollmentId: string) => void;
  onDelete?: (enrollmentId: string) => void;
  approvingId?: string;
  deletingId?: string;
  variant?: 'pending' | 'rejected';
  emptyMessage?: string;
}

export const EnrollmentList: React.FC<EnrollmentListProps> = ({
  enrollments,
  loading = false,
  onApprove,
  onDelete,
  approvingId,
  deletingId,
  variant = 'pending',
  emptyMessage,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (enrollments.length === 0) {
    return (
      <Card
        sx={{
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: 6,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15, 23, 42, 0.04)',
              color: 'text.disabled',
              mb: 2,
            }}
          >
            <InboxIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h6" component="p" color="text.secondary" gutterBottom>
            {emptyMessage ?? 'No hay enrollments pendientes'}
          </Typography>
          {variant === 'pending' && (
            <Typography variant="body2" color="text.secondary">
              Todo listo por ahora.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {enrollments.map((enrollment) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={enrollment.enrollmentId}>
          <EnrollmentCard
            enrollment={enrollment}
            variant={variant}
            onApprove={() => onApprove?.(enrollment.enrollmentId)}
            onDelete={() => onDelete?.(enrollment.enrollmentId)}
            approving={approvingId === enrollment.enrollmentId}
            deleting={deletingId === enrollment.enrollmentId}
          />
        </Grid>
      ))}
    </Grid>
  );
};
